import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";

import { useDevelopmentFixtures } from "../api/client";

export type LiveConnection = "connecting" | "live" | "polling" | "offline" | "fixtures";

interface LiveSyncOptions {
  onRefresh: () => Promise<void>;
  pollIntervalMs?: number;
  onSessionExpired?: () => void;
  aggregateTypes?: readonly string[];
}

export function useLiveSync({ onRefresh, pollIntervalMs = 30000, onSessionExpired, aggregateTypes }: LiveSyncOptions): LiveConnection {
  const [status, setStatus] = useState<LiveConnection>(useDevelopmentFixtures ? "fixtures" : "connecting");
  const refreshRef = useRef(onRefresh);
  const sessionExpiredRef = useRef(onSessionExpired);
  const aggregateTypesRef = useRef(aggregateTypes);
  refreshRef.current = onRefresh;
  sessionExpiredRef.current = onSessionExpired;
  aggregateTypesRef.current = aggregateTypes;

  useEffect(() => {
    if (useDevelopmentFixtures) return undefined;

    let pollingTimer = 0;
    let stopped = false;
    let socket: Socket | null = io({
      path: "/api/v1/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 15_000,
      timeout: 10_000,
    });

    const stopPolling = () => {
      if (pollingTimer) window.clearInterval(pollingTimer);
      pollingTimer = 0;
    };
    const startPolling = () => {
      if (stopped || pollingTimer) return;
      setStatus(navigator.onLine ? "polling" : "offline");
      pollingTimer = window.setInterval(() => {
        if (navigator.onLine) void refreshRef.current();
      }, pollIntervalMs);
    };
    const expireSession = () => {
      stopped = true;
      stopPolling();
      socket?.disconnect();
      socket = null;
      setStatus("offline");
      if (sessionExpiredRef.current) sessionExpiredRef.current();
      else window.location.assign("/login");
    };

    socket.on("connect", () => {
      stopPolling();
      setStatus("live");
    });
    socket.on("update", (notification: { aggregateType?: string }) => {
      const acceptedTypes = aggregateTypesRef.current;
      if (acceptedTypes?.length && (!notification.aggregateType || !acceptedTypes.includes(notification.aggregateType))) return;
      void refreshRef.current();
    });
    socket.on("session_expired", expireSession);
    socket.on("connect_error", startPolling);
    socket.on("disconnect", (reason) => {
      if (!stopped && reason !== "io client disconnect") startPolling();
    });

    const handleOnline = () => {
      if (stopped || socket?.connected) return;
      setStatus("connecting");
      socket?.connect();
    };
    const handleOffline = () => {
      setStatus("offline");
      socket?.disconnect();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      stopped = true;
      stopPolling();
      socket?.removeAllListeners();
      socket?.disconnect();
      socket = null;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [pollIntervalMs]);

  return status;
}
